import { useState } from 'react'
import {
    Box,
    Button,
    Field,
    HStack,
    Input,
    NativeSelect,
    Separator,
    Stack,
    Switch,
    Text,
} from '@chakra-ui/react'
import { UserSection } from './UserSection'
import { inviteSeller, setSellerActive, setSellerRole, setSysAdmin } from '../../services/userAdmin'
import { accountLabel } from '../../utils/userDisplay'
import { useTranslation } from '../../i18n'

const SALES_ROLES = ['seller', 'salesmanager']
const roleKey = (role) => `users.platform.salesRole${role[0].toUpperCase()}${role.slice(1)}`

function SalesEnrolForm({ user, act, onDone }) {
    const { t } = useTranslation()
    const [name, setName] = useState(accountLabel(user, t))
    const [kind, setKind] = useState('external')
    const [role, setRole] = useState('seller')
    const [commission, setCommission] = useState('10')
    const [busy, setBusy] = useState(false)

    const submit = async (e) => {
        e.preventDefault()
        setBusy(true)
        const ok = await act(() => inviteSeller({
            email: user.email, name: name.trim(), kind, role, commissionPct: Number(commission),
        }), t('users.platform.enrolled'))
        setBusy(false)
        if (ok) onDone()
    }

    return (
        <Stack as="form" onSubmit={submit} gap={3} maxW="420px">
            <Field.Root required>
                <Field.Label>{t('users.platform.enrolName')}</Field.Label>
                <Input size="sm" value={name} onChange={(e) => setName(e.target.value)} />
            </Field.Root>
            <Field.Root>
                <Field.Label>{t('users.platform.enrolKind')}</Field.Label>
                <NativeSelect.Root size="sm">
                    <NativeSelect.Field value={kind} onChange={(e) => setKind(e.target.value)}>
                        <option value="external">{t('users.platform.enrolKindExternal')}</option>
                        <option value="internal">{t('users.platform.enrolKindInternal')}</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                </NativeSelect.Root>
            </Field.Root>
            <Field.Root>
                <Field.Label>{t('users.platform.enrolRole')}</Field.Label>
                <NativeSelect.Root size="sm">
                    <NativeSelect.Field value={role} onChange={(e) => setRole(e.target.value)}>
                        {SALES_ROLES.map((r) => <option key={r} value={r}>{t(roleKey(r))}</option>)}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                </NativeSelect.Root>
            </Field.Root>
            <Field.Root>
                <Field.Label>{t('users.platform.enrolCommission')}</Field.Label>
                <Input size="sm" type="number" min="0" max="100" step="0.5" value={commission}
                    onChange={(e) => setCommission(e.target.value)} />
            </Field.Root>
            <Button size="sm" variant="outline" type="submit" loading={busy} disabled={!name.trim()} alignSelf="flex-start">
                {t('users.platform.enrolSubmit')}
            </Button>
        </Stack>
    )
}

function SalesControls({ user, isSelf, act }) {
    const { t } = useTranslation()
    const [busy, setBusy] = useState(null)
    const [enrolling, setEnrolling] = useState(false)
    const { sales } = user

    const run = async (key, fn, success) => {
        setBusy(key)
        await act(fn, success)
        setBusy(null)
    }

    if (!sales) {
        if (!user.email) return <Text fontSize="sm" color="var(--mc-text-muted)">{t('users.platform.needsEmail')}</Text>
        return (
            <Stack gap={3}>
                <Text fontSize="sm" color="var(--mc-text-muted)">{t('users.platform.salesNone')}</Text>
                {enrolling ? (
                    <SalesEnrolForm user={user} act={act} onDone={() => setEnrolling(false)} />
                ) : (
                    <Button size="sm" variant="outline" alignSelf="flex-start" disabled={isSelf}
                        onClick={() => setEnrolling(true)}>
                        {t('users.platform.enrol')}
                    </Button>
                )}
            </Stack>
        )
    }

    return (
        <HStack gap={3} wrap="wrap">
            <NativeSelect.Root size="sm" width="200px" disabled={isSelf || busy === 'role'}>
                <NativeSelect.Field
                    aria-label={t('users.platform.enrolRole')}
                    value={sales.role || 'seller'}
                    onChange={(e) => run('role',
                        () => setSellerRole({ uid: user.uid, role: e.target.value }),
                        t('users.platform.salesRoleChanged'))}
                >
                    {SALES_ROLES.map((r) => <option key={r} value={r}>{t(roleKey(r))}</option>)}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
            </NativeSelect.Root>
            <Text fontSize="sm" color="var(--mc-text)">
                {sales.active ? t('users.platform.salesActive') : t('users.platform.salesInactive')}
            </Text>
            <Button size="sm" variant="outline" disabled={isSelf} loading={busy === 'active'}
                onClick={() => run('active',
                    () => setSellerActive({ uid: user.uid, active: !sales.active }),
                    t('users.platform.salesActiveChanged'))}>
                {sales.active ? t('users.platform.salesDeactivate') : t('users.platform.salesActivate')}
            </Button>
        </HStack>
    )
}

export function PlatformRolesCard({ user, isSelf, act }) {
    const { t } = useTranslation()
    const [busy, setBusy] = useState(false)
    // The server's own answer: yourself, or the last sysadmin.
    const refusal = user.sysAdminRefusal

    const toggleSysAdmin = async (checked) => {
        setBusy(true)
        await act(() => setSysAdmin({ uid: user.uid, admin: checked }),
            checked ? t('users.platform.granted') : t('users.platform.revoked'))
        setBusy(false)
    }

    return (
        <UserSection id="user-platform" title={t('users.platform.title')}>
            <Box>
                <Switch.Root
                    checked={user.sysadmin}
                    disabled={!!refusal || busy}
                    onCheckedChange={(d) => toggleSysAdmin(d.checked)}
                >
                    <Switch.HiddenInput />
                    <Switch.Control />
                    <Switch.Label>{t('users.platform.sysadmin')}</Switch.Label>
                </Switch.Root>
                <Text fontSize="sm" color="var(--mc-text-muted)" mt={1}>
                    {refusal || t('users.platform.sysadminHelp')}
                </Text>
            </Box>
            <Separator />
            <Stack gap={2}>
                <Text fontWeight="600" color="var(--mc-text)">{t('users.platform.sales')}</Text>
                <SalesControls user={user} isSelf={isSelf} act={act} />
            </Stack>
        </UserSection>
    )
}
